import { KeyValue } from "@/models";
import { Autocomplete, TextField } from "@mui/material";
import React from "react";
import { useRoomAutocomplete } from "./room-autocomplete.hook";
import { autocompleteStyles, inputStyles } from "./room-autocomplete.styles";
import { RoomAutocompleteProps } from "./room-autocomplete.types";

/**
 * Room Autocomplete Component
 *
 * A reusable autocomplete component that fetches and displays rooms filtered by owner/user.
 * Includes API integration and handles loading states automatically.
 *
 * @example
 * ```tsx
 * <RoomAutocompleteComponent
 *   value={roomId}
 *   targetUserId={userId}
 *   onChange={(room) => setRoomId(room?.key ?? "")}
 *   placeholder="Select a room"
 * />
 * ```
 */
export const RoomAutocompleteComponent: React.FC<RoomAutocompleteProps> = ({
  value,
  targetUserId,
  enabled = true,
  onChange,
  placeholder = "Select room",
  disabled = false,
  noOptionsText = "No rooms available",
  roomNameFallback,
}) => {
  const { roomsOptions, selectedRoom, isLoading } = useRoomAutocomplete({
    value,
    targetUserId,
    enabled,
    roomNameFallback,
  });

  return (
    <Autocomplete
      value={selectedRoom}
      options={roomsOptions}
      onChange={(_e, v) => onChange(v)}
      disabled={disabled || isLoading}
      loading={isLoading}
      disableClearable={!selectedRoom}
      isOptionEqualToValue={(option, value) => option.key === value.key}
      noOptionsText={noOptionsText}
      sx={autocompleteStyles}
      renderInput={(params) => (
        <TextField
          {...params}
          inputProps={{
            ...params.inputProps,
            style: inputStyles,
            title: selectedRoom?.value || "",
          }}
          size="small"
          placeholder={placeholder}
        />
      )}
      getOptionLabel={({ value }: KeyValue) => value}
    />
  );
};
